/**
 * Private Discount Slider Component
 * Interactive slider for adjusting private company discount with real-time recalculation
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Percent, HelpCircle, Loader } from 'lucide-react';

const PrivateDiscountSlider = ({
  discount,
  onDiscountChange,
  onShowDiscountInfo,
  sliderCalculating,
  result
}) => {
  return (
    <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-300 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Percent className="text-purple-600" size={20} />
          <label className="text-sm font-semibold text-gray-800">
            Private Company Discount
          </label>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onShowDiscountInfo(true);
            }}
            className="text-purple-600 hover:text-purple-700"
          >
            <HelpCircle size={16} />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-600">
            {discount === 0 && "Using public company multiples (no discount)"}
            {discount > 0 && discount < 25 && "Lower discount = Higher valuation"}
            {discount === 25 && "Standard private company discount applied"}
            {discount > 25 && "Higher discount = More conservative valuation"}
          </span>
          {sliderCalculating && (
            <Loader className="animate-spin text-purple-600" size={16} />
          )}
          <span className="text-2xl font-bold text-purple-600">
            {discount}%
          </span>
        </div>
      </div>

      <input
        type="range"
        min="0"
        max="50"
        step="5"
        value={discount}
        onChange={onDiscountChange}
        className="w-full h-3 bg-gradient-to-r from-purple-200 to-pink-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
        style={{
          background: `linear-gradient(to right, #9333ea 0%, #9333ea ${discount * 2}%, #e9d5ff ${discount * 2}%, #e9d5ff 100%)`
        }}
      />

      <div className="flex justify-between text-xs text-gray-600 mt-2">
        <span>0% (No discount)</span>
        <span>25% (Standard)</span>
        <span>50% (Maximum)</span>
      </div>

      {result && (
        <div className="mt-3 pt-3 border-t border-purple-200">
          {sliderCalculating && (
            <p className="text-xs text-purple-600 flex items-center gap-1">
              <Loader className="animate-spin" size={12} />
              Updating valuation...
            </p>
          )}
          {!sliderCalculating && (
            <p className="text-xs text-green-600">
              ✓ Valuation updated
            </p>
          )}
        </div>
      )}
    </div>
  );
};

PrivateDiscountSlider.propTypes = {
  discount: PropTypes.number.isRequired,
  onDiscountChange: PropTypes.func.isRequired,
  onShowDiscountInfo: PropTypes.func.isRequired,
  sliderCalculating: PropTypes.bool.isRequired,
  result: PropTypes.object
};

export default PrivateDiscountSlider;
